import type { ComputedRef, Ref } from 'vue'

import {
  buildMentionReferenceLines,
  type ConsistencyAssetLookups,
  extractPromptMentionNames,
  findMentionedConsistencyAssets,
  getSceneBoundConsistencyAssets,
  normalizeConsistencyText,
  replacePromptMentions,
} from './consistencyAssets'
import { normalizePromptFragment } from './promptUtils'
import {
  buildSceneImageCreativePrompt,
  buildScenePromptEngineeringContext,
  buildSceneVideoCreativePrompt,
} from './promptEngineering'
import { resolveSceneVideoDuration } from './sceneVideo'
import type {
  ConsistencyAssetCollection,
  ConsistencyAssetDraft,
  StoryboardPromptReferencePayload,
  StoryboardSceneDraft,
} from './types'

interface UseStoryboardSceneGenerationOptions {
  projectId: number
  workflow: Ref<any>
  storyboardDraftScenes: Ref<StoryboardSceneDraft[]>
  sceneImageInputs: Ref<Record<number, string>>
  consistencyAssets: Ref<ConsistencyAssetCollection>
  consistencyAssetLookups: ComputedRef<ConsistencyAssetLookups>
  sceneVideoGenerating: Ref<Record<number, boolean>>
  trackedSceneVideoTaskIds: Ref<Record<number, string>>
  ensureStoryboardScenesReady: () => Promise<boolean>
  ensureConsistencyAssetsReady: () => Promise<boolean>
  isSceneVideoTaskActive: (sceneNumber: number) => boolean
  buildSceneKnowledgePrompt: (
    sceneNumber: number,
    promptOverride?: string,
    extraLines?: string[],
    fallbackToSceneDefault?: boolean,
  ) => string | undefined
  refreshSceneVideoTaskStatus: (sceneNumber: number) => Promise<void>
  generateStoryboardSceneVideoApi: (projectId: number, sceneNumber: number, payload: {
    prompt?: string
    duration: number
    video_model_id?: string
    referenced_assets?: StoryboardPromptReferencePayload['referenced_assets']
  }) => Promise<{ workflow: any; task?: Record<string, any> | null; already_running?: boolean }>
  info: (message: string) => void
  success: (message: string) => void
  error: (message: string) => void
}

export function useStoryboardSceneGeneration(options: UseStoryboardSceneGenerationOptions) {
  function buildMissingMentionErrorMessage(missingAssetMentions: string[], missingImageMentions: string[]) {
    const parts: string[] = []

    if (missingAssetMentions.length) {
      parts.push(`未找到官方资产：${missingAssetMentions.map((name) => `@${name}`).join('、')}`)
    }
    if (missingImageMentions.length) {
      parts.push(`尚未绑定官方参考图：${missingImageMentions.map((name) => `@${name}`).join('、')}`)
    }

    if (!parts.length) {
      return ''
    }

    return `${parts.join('；')}。先在“一致性资产”里为这些对象创建并绑定官方参考图，再生成图片。`
  }

  function getUserSuppliedReferenceImageUrls(sceneNumber: number, scene: StoryboardSceneDraft) {
    const urls: string[] = []
    const inputUrl = String(options.sceneImageInputs.value[sceneNumber] || '').trim()
    const selectedImageUrl = String(scene.background_image_url || '').trim()
    const selectedImageSource = String(scene.selected_image_source || '').trim()

    const pushUnique = (value: string) => {
      if (!value || urls.includes(value)) {
        return
      }
      urls.push(value)
    }

    if (inputUrl && inputUrl !== selectedImageUrl) {
      pushUnique(inputUrl)
    }

    if (
      selectedImageUrl
      && (
        selectedImageSource === 'manual'
        || selectedImageSource === 'manual_url'
        || selectedImageSource === 'uploaded'
        || selectedImageSource.startsWith('uploaded_file:')
      )
    ) {
      pushUnique(selectedImageUrl)
    }

    return urls.slice(0, 3)
  }

  function buildScenePromptContext(scene: StoryboardSceneDraft | Record<string, any>) {
    return buildScenePromptEngineeringContext({
      scene,
      consistencyAssets: options.consistencyAssets.value,
      consistencyAssetLookups: options.consistencyAssetLookups.value,
    })
  }

  function buildSceneGenerationRequest(sceneNumber: number, purpose: 'image' | 'video'): StoryboardPromptReferencePayload {
    const scene = options.storyboardDraftScenes.value.find((item) => item.scene_number === sceneNumber)
    if (!scene) {
      return {}
    }

    const originalPrompt = purpose === 'image'
      ? normalizePromptFragment(scene.background_prompt)
      : normalizePromptFragment(scene.video_prompt)

    const promptSeed = purpose === 'image'
      ? originalPrompt
      : normalizePromptFragment(scene.video_prompt) || buildSceneVideoCreativePrompt(buildScenePromptContext(scene))

    const promptMentionNames = extractPromptMentionNames(promptSeed)
    const mentioned = findMentionedConsistencyAssets(promptSeed, options.consistencyAssets.value)
    const explicitAssets = {
      character: getSceneBoundConsistencyAssets(scene, 'character', options.consistencyAssets.value),
      scene: getSceneBoundConsistencyAssets(scene, 'scene', options.consistencyAssets.value),
      prop: getSceneBoundConsistencyAssets(scene, 'prop', options.consistencyAssets.value),
    }
    const mergeUniqueAssets = (assets: ConsistencyAssetDraft[]) => Array.from(new Map(assets.map((item) => [item.id, item] as const)).values())
    const referencedAssetGroups = {
      character: mergeUniqueAssets([...explicitAssets.character, ...mentioned.character]),
      scene: mergeUniqueAssets([...explicitAssets.scene, ...mentioned.scene]),
      prop: mergeUniqueAssets([...explicitAssets.prop, ...mentioned.prop]),
    }
    if (purpose === 'image' && promptMentionNames.length) {
      const mentionedAssets = mergeUniqueAssets([
        ...mentioned.character,
        ...mentioned.scene,
        ...mentioned.prop,
      ])
      const matchedMentionNames = new Set(
        mentionedAssets
          .map((item) => normalizeConsistencyText(item.name || item.id))
          .filter(Boolean)
      )
      const missingAssetMentions = promptMentionNames.filter((name) => !matchedMentionNames.has(normalizeConsistencyText(name)))
      const missingImageMentions = mentionedAssets
        .filter((item) => !String(item.image_url || '').trim())
        .map((item) => String(item.name || item.id || '').trim())
        .filter(Boolean)

      const errorMessage = buildMissingMentionErrorMessage(missingAssetMentions, Array.from(new Set(missingImageMentions)))
      if (errorMessage) {
        throw new Error(errorMessage)
      }
    }
    const promptWithoutMentions = replacePromptMentions(promptSeed, [
      ...referencedAssetGroups.character,
      ...referencedAssetGroups.scene,
      ...referencedAssetGroups.prop,
    ]).trim()
    const mentionLines = buildMentionReferenceLines([
      ...referencedAssetGroups.character,
      ...referencedAssetGroups.scene,
      ...referencedAssetGroups.prop,
    ])
    const referencedAssets = {
      character_ids: referencedAssetGroups.character.map((item) => item.id),
      scene_ids: referencedAssetGroups.scene.map((item) => item.id),
      prop_ids: referencedAssetGroups.prop.map((item) => item.id),
    }
    const prompt = purpose === 'image'
      ? [
        promptWithoutMentions,
        ...(mentionLines.length ? ['强制引用官方资产：', ...mentionLines] : []),
      ].filter(Boolean).join('\n')
      : [
        promptWithoutMentions,
        ...(mentionLines.length ? ['强制引用官方资产：', ...mentionLines] : []),
      ].filter(Boolean).join('\n')
    const referenceImageUrls = purpose === 'image'
      ? getUserSuppliedReferenceImageUrls(sceneNumber, scene)
      : []

    return {
      prompt: prompt || undefined,
      original_prompt: purpose === 'image' ? originalPrompt || undefined : undefined,
      reference_image_urls: referenceImageUrls.length ? referenceImageUrls : undefined,
      referenced_assets: Object.values(referencedAssets).some((items) => items.length)
        ? referencedAssets
        : undefined,
    }
  }

  function getStoryboardSceneBindingLabels(scene: Record<string, any>) {
    return [
      ...getSceneBoundConsistencyAssets(scene, 'character', options.consistencyAssets.value).map((item) => `角色 · ${item.name}`),
      ...getSceneBoundConsistencyAssets(scene, 'scene', options.consistencyAssets.value).map((item) => `场景 · ${item.name}`),
      ...getSceneBoundConsistencyAssets(scene, 'prop', options.consistencyAssets.value).map((item) => `道具 · ${item.name}`),
    ]
  }

  function getStoryboardSceneMeta(scene: Record<string, any>) {
    return [scene.shot_type, scene.camera_angle, scene.camera_movement]
      .map((item) => String(item || '').trim())
      .filter(Boolean)
      .slice(0, 3)
  }

  async function handleGenerateSceneVideo(sceneNumber: number, videoModelId?: string) {
    const ready = await options.ensureStoryboardScenesReady()
    if (!ready) {
      return
    }

    const consistencyReady = await options.ensureConsistencyAssetsReady()
    if (!consistencyReady) {
      return
    }

    if (options.isSceneVideoTaskActive(sceneNumber)) {
      options.info(`镜头 ${sceneNumber} 已有视频生成任务正在处理中`)
      return
    }

    options.sceneVideoGenerating.value = { ...options.sceneVideoGenerating.value, [sceneNumber]: true }
    try {
      const scene = options.storyboardDraftScenes.value.find((item) => item.scene_number === sceneNumber)
      const requestPayload = buildSceneGenerationRequest(sceneNumber, 'video')
      const duration = resolveSceneVideoDuration(scene)
      const response = await options.generateStoryboardSceneVideoApi(options.projectId, sceneNumber, {
        prompt: requestPayload.prompt,
        duration,
        video_model_id: videoModelId,
        referenced_assets: requestPayload.referenced_assets,
      })
      options.storyboardDraftScenes.value = options.storyboardDraftScenes.value.map((item) => (
        item.scene_number === sceneNumber
          ? { ...item, duration }
          : item
      ))
      options.workflow.value = response.workflow
      const taskId = String(response.task?.task_id || '').trim()
      if (taskId) {
        options.trackedSceneVideoTaskIds.value = {
          ...options.trackedSceneVideoTaskIds.value,
          [sceneNumber]: taskId,
        }
        void options.refreshSceneVideoTaskStatus(sceneNumber)
      }
      options.success(response.already_running ? `镜头 ${sceneNumber} 视频任务已在处理中` : `镜头 ${sceneNumber} 视频已加入生成队列`)
    } catch (error: any) {
      options.error(error?.response?.data?.detail || error?.response?.data?.message || '分镜视频生成失败')
    } finally {
      options.sceneVideoGenerating.value = { ...options.sceneVideoGenerating.value, [sceneNumber]: false }
    }
  }

  return {
    buildSceneGenerationRequest,
    getStoryboardSceneBindingLabels,
    getStoryboardSceneMeta,
    handleGenerateSceneVideo,
  }
}